import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./useApi";
import {
  Journey,
  JourneyDetail,
  JourneyInput,
  JourneyUpdateInput,
  JourneyPublishInput,
} from "../types/journey";
import { buildApiUrl } from "../api";

// Query key factory for consistent keys
export const journeyKeys = {
  all: ["journeys"] as const,
  lists: () => [...journeyKeys.all, "list"] as const,
  list: (teamId: string) => [...journeyKeys.lists(), teamId] as const,
  details: () => [...journeyKeys.all, "detail"] as const,
  detail: (teamId: string, journeyId: string) =>
    [...journeyKeys.details(), teamId, journeyId] as const,
  templates: () => [...journeyKeys.all, "templates"] as const,
  template: (teamId: string) => [...journeyKeys.templates(), teamId] as const,
};

/**
 * Hook for fetching the list of journeys for a team
 */
export function useJourneys(teamId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: journeyKeys.list(teamId || ""),
    queryFn: async (): Promise<Journey[]> => {
      const url = buildApiUrl("/v2/slack/journeys", teamId);
      const response = await api.get<Journey[]>(url);
      return response.data;
    },
    enabled: !!teamId,
    // Disable automatic refetching when window regains focus
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching details of a specific journey
 */
export function useJourneyDetail(teamId: string | null, journeyId: string) {
  const api = useApi();

  return useQuery({
    queryKey: journeyKeys.detail(teamId || "", journeyId),
    queryFn: async (): Promise<JourneyDetail> => {
      const url = buildApiUrl(`/v2/slack/journeys/${journeyId}`, teamId);
      const response = await api.get<JourneyDetail>(url);
      return response.data;
    },
    enabled: !!teamId && !!journeyId,
    // Disable automatic refetching when window regains focus
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for creating a new journey
 */
export function useCreateJourney() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (journey: JourneyInput): Promise<Journey> => {
      const url = buildApiUrl("/v2/slack/journeys", journey.team);
      const response = await api.post<Journey>(url, journey);
      return response.data;
    },
    onSuccess: (data, variables) => {
      console.log("data", data);
      // Invalidate the journeys list for the team
      queryClient.invalidateQueries({
        queryKey: journeyKeys.list(variables.team),
      });
    },
  });
}

/**
 * Hook for updating an existing journey
 */
export function useUpdateJourney(teamId: string | null, journeyId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateData: JourneyUpdateInput): Promise<Journey> => {
      const url = buildApiUrl(`/v2/slack/journeys/${journeyId}`, teamId);
      const response = await api.put<Journey>(url, updateData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both the list and the detail queries
      if (teamId) {
        queryClient.invalidateQueries({
          queryKey: journeyKeys.list(teamId),
        });
        queryClient.invalidateQueries({
          queryKey: journeyKeys.detail(teamId, journeyId),
        });
      }
    },
  });
}

/**
 * Hook for deleting a journey
 */
export function useDeleteJourney(teamId: string | null) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (journeyId: string): Promise<void> => {
      const url = buildApiUrl(`/v2/slack/journeys/${journeyId}`, teamId);
      await api.delete(url);
    },
    onSuccess: (_, journeyId) => {
      // Invalidate the journeys list
      if (teamId) {
        queryClient.invalidateQueries({
          queryKey: journeyKeys.list(teamId),
        });
        // Also remove the journey detail from the cache
        queryClient.removeQueries({
          queryKey: journeyKeys.detail(teamId, journeyId),
        });
      }
    },
  });
}

/**
 * Hook for publishing a journey
 */
export function usePublishJourney(teamId: string | null, journeyId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (publishData: JourneyPublishInput) => {
      const url = buildApiUrl(
        `/v2/slack/journeys/${journeyId}/publish`,
        teamId,
      );
      const response = await api.post(url, publishData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both the list and the detail queries
      if (teamId) {
        queryClient.invalidateQueries({
          queryKey: journeyKeys.list(teamId),
        });
        queryClient.invalidateQueries({
          queryKey: journeyKeys.detail(teamId, journeyId),
        });
      }
    },
  });
}

/**
 * Hook for fetching journey templates
 *
 * Templates are journeys that can be used as starting points for new journeys.
 * They typically have the 'template' flag set to true in the API.
 */
export function useJourneyTemplates(teamId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: journeyKeys.template(teamId || ""),
    queryFn: async (): Promise<Journey[]> => {
      // Fetch journeys with template=true query parameter
      // Need to add template=true to the URL manually since buildApiUrl only handles teamId
      const baseUrl = buildApiUrl("/v2/slack/journeys", teamId);
      const url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}template=true`;
      const response = await api.get<Journey[]>(url);
      return response.data;
    },
    enabled: !!teamId,
    // Disable automatic refetching when window regains focus
    refetchOnWindowFocus: false,
  });
}
